/**
 * The one referral link (Bez, 2026-10-03): /r/<handle or wallet> for the profile, competitions and NFTs. Pure parts, tested.
 * The inviter a visitor arrives with is stored first-touch (refstore.ts) and bound to the wallet for good at its first CADINU
 * sign-in (social API, referrals/<wallet>). Old ?ref= links keep working; they are no longer shown anywhere.
 */
const ADDR = /^0x[0-9a-fA-F]{40}$/

/** What the link earns, in one line, the same everywhere it is shown. */
export const REFERRAL_EARNS =
  'Earns a share of the creation fee on competitions created with it, a commission on CADINU NFT sales, and credits you with the people who join CADINU through it.'

export const referralPath = (id: string) => `/r/${encodeURIComponent(id)}`
export const referralUrl = (origin: string, id: string) => `${origin.replace(/\/$/, '')}${referralPath(id)}`

export type ReferrerSource = 'bound' | 'link' | 'invite'

/**
 * The referrer to prefill: the inviter bound to the wallet first (permanent), then an explicit old ?ref= link, then the
 * first-touch invite stored in this browser. Never the buyer or the recipient itself, never a malformed address.
 */
export function pickReferrer(i: {
  bound?: string | null
  query?: unknown
  stored?: string | null
  self?: string | null
  recipient?: string | null
}): { address: `0x${string}`; source: ReferrerSource } | null {
  const not = [i.self, i.recipient].filter(Boolean).map((a) => String(a).toLowerCase())
  const ok = (a: unknown): a is `0x${string}` => typeof a === 'string' && ADDR.test(a) && !/^0x0{40}$/i.test(a) && !not.includes(a.toLowerCase())
  if (ok(i.bound)) return { address: i.bound, source: 'bound' }
  if (i.bound && typeof i.bound === 'string' && ADDR.test(i.bound)) return null // bound to the buyer/recipient itself: nothing, never another
  if (ok(i.query)) return { address: i.query, source: 'link' }
  if (ok(i.stored)) return { address: i.stored, source: 'invite' }
  return null
}

/** "Invited by @alice" / "Invited by 0x1234…abcd". */
export function inviterLabel(inv: { address: string; handle?: string | null } | null | undefined): string | null {
  if (!inv?.address) return null
  return `Invited by ${inv.handle ? `@${inv.handle}` : `${inv.address.slice(0, 6)}…${inv.address.slice(-4)}`}`
}
